import { useFormikContext } from "formik";
import { FormField } from "@/components/common/form-field";
import { DateField, PhoneField, SsnField } from "@/components/common/masked-form-field";
import {
  CheckboxField,
  RadioField,
  SelectField,
} from "@/features/portal/components/onboarding/onboarding-fields";
import {
  PASSPORT_OPTIONS,
  PREF_CONTACT_OPTIONS,
  STATE_OPTIONS,
} from "@/features/portal/components/onboarding/onboarding-options";
import {
  type TabProps,
  useBlurSave,
} from "@/features/portal/components/onboarding/tabs/tab-shared";
import type { OnboardingValues } from "@/features/portal/components/onboarding/wizard-schema";

const MARITAL_OPTIONS = [
  { value: "UNMARRIED", label: "Unmarried" },
  { value: "MARRIED", label: "Married" },
];

/** Spouse Information tab (individual clients). Spouse fields appear only when Married. */
export function SpouseTab({ save }: TabProps) {
  const { values } = useFormikContext<OnboardingValues>();
  const onBlur = useBlurSave(save);
  const isMarried = values.marital_status === "MARRIED";

  return (
    <div className="grid gap-6">
      <RadioField
        name="marital_status"
        label="Marital Status"
        options={MARITAL_OPTIONS}
        onCommit={save}
      />

      {isMarried && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              name="date_of_marriage"
              label="Date of Marriage"
              onBlur={onBlur("date_of_marriage")}
            />
          </div>
          <CheckboxField
            name="in_community_property_state"
            label="Lived in a community-property state in the last 10 years"
            onCommit={save}
          />

          <div className="grid gap-4 border-t pt-4 sm:grid-cols-3">
            <FormField
              name="spouse_first_name"
              label="Spouse First Name"
              onBlur={onBlur("spouse_first_name")}
            />
            <FormField
              name="spouse_middle_initial"
              label="Spouse Middle Initial"
              maxLength={1}
              onBlur={onBlur("spouse_middle_initial")}
            />
            <FormField
              name="spouse_last_name"
              label="Spouse Last Name"
              onBlur={onBlur("spouse_last_name")}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SsnField name="spouse_ssn" label="Spouse SSN" onBlur={onBlur("spouse_ssn")} />
            <DateField
              name="spouse_date_of_birth"
              label="Spouse Date of Birth"
              onBlur={onBlur("spouse_date_of_birth")}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              name="spouse_driver_license"
              label="Spouse Driver's License #"
              maxLength={20}
              onBlur={onBlur("spouse_driver_license")}
            />
            <SelectField
              name="spouse_license_state"
              label="Spouse License State"
              options={STATE_OPTIONS}
              placeholder="Select state"
              onCommit={save}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <PhoneField
              name="spouse_home_phone"
              label="Spouse Home Phone"
              onBlur={onBlur("spouse_home_phone")}
            />
            <PhoneField
              name="spouse_mobile_phone"
              label="Spouse Mobile Phone"
              onBlur={onBlur("spouse_mobile_phone")}
            />
            <PhoneField
              name="spouse_work_phone"
              label="Spouse Work Phone"
              onBlur={onBlur("spouse_work_phone")}
            />
            <PhoneField
              name="spouse_work_mobile"
              label="Spouse Work Mobile"
              onBlur={onBlur("spouse_work_mobile")}
            />
            <PhoneField
              name="spouse_fax_number"
              label="Spouse Fax"
              onBlur={onBlur("spouse_fax_number")}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              name="spouse_email"
              label="Spouse Email"
              type="email"
              onBlur={onBlur("spouse_email")}
            />
            <FormField
              name="spouse_salutation"
              label="Spouse Salutation"
              onBlur={onBlur("spouse_salutation")}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              name="spouse_preferred_contact_method"
              label="Spouse Preferred Contact Method"
              options={PREF_CONTACT_OPTIONS}
              onCommit={save}
            />
            <SelectField
              name="spouse_passport_on_file"
              label="Spouse Passport on File"
              options={PASSPORT_OPTIONS}
              onCommit={save}
            />
          </div>

          <div className="grid gap-3 border-t pt-4">
            <CheckboxField
              name="spouse_is_trustee"
              label="Spouse is a trustee"
              onCommit={save}
            />
            {values.spouse_is_trustee && (
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  name="spouse_trustee_name"
                  label="Spouse Trustee Name"
                  onBlur={onBlur("spouse_trustee_name")}
                />
                <FormField
                  name="spouse_trustee_role"
                  label="Spouse Trustee Role"
                  onBlur={onBlur("spouse_trustee_role")}
                />
              </div>
            )}
          </div>

          <div className="grid gap-3 border-t pt-4">
            <CheckboxField
              name="spouse_is_deceased"
              label="Spouse is deceased"
              onCommit={save}
            />
            {values.spouse_is_deceased && (
              <div className="sm:max-w-xs">
                <DateField
                  name="spouse_date_of_death"
                  label="Spouse Date of Death"
                  onBlur={onBlur("spouse_date_of_death")}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
